// #region: Class definition *********************************************************************/
/**
 * @class ContentPager
 * @description Custom web component that provides paging functionality for content.
 * Can display page numbers as buttons or a dropdown select based on available space.
 * 
 * @extends WebComponent
 *
 * @example
 * <content-pager current-page="1" total-pages="10" max-visible="5">
 * </content-pager>
 *
 * @property {string} current-page - The current active page number.
 * @property {string} total-pages - The total number of pages.
 * @property {string} max-visible - Maximum number of page buttons visible.
 *
 * @remarks
 *     All attributes are optional and have sensible defaults.
 */
class ContentPager extends WebComponent {

    // #region: Static Data **********************************************************************/
    /**
     * Default size (in pixels) for page numbers in the pager.
     * @type {number}
     * @static
     * @memberof ContentPager
     */
    static BUTTON_SIZE = 32;

    /**
     * Default maximum visible page numbers in the pager.
     * @type {number}
     * @static
     * @memberof ContentPager
     */
    static DEFAULT_MAX_VISIBLE = 5;
    // #endregion ********************************************************************************/

    // #region: Private Fields *******************************************************************/
    /**
     * Button to navigate to the first page.
     * @type {HTMLButtonElement}
     * 
     * @memberof ContentPager
     * @private
     */
    #firstButton;

    /**
     * Button to navigate to the last page.
     * @type {HTMLButtonElement}
     * 
     * @memberof ContentPager
     * @private
     */
    #lastButton;

    /**
     * Cached locale resources for this component.
     * @type {Object}
     * @private
     * @memberof DateTimePicker
     */
    #locales;

    /**
     * Calculated width of a page number button.
     * @type {number}
     * 
     * @memberof ContentPager
     * @private
     */
    #navigationButtonWidth;

    /**
     * Button to navigate to the next page.
     * @type {HTMLButtonElement}
     * 
     * @memberof ContentPager
     * @private
     */
    #nextButton;

    /**
     * The DOM element container for page number buttons or select dropdown.
     * @type {HTMLDivElement}
     * 
     * @memberof ContentPager
     * @private
     */
    #pageNumbers;

    /**
     * The DOM element displaying the total page count.
     * @type {HTMLDivElement}
     * 
     * @memberof ContentPager
     * @private
     */
    #pageCount;

    /**
     * Button to navigate to the previous page.
     * @type {HTMLButtonElement}
     * 
     * @memberof ContentPager
     * @private
     */
    #prevButton;

    /**
     * ResizeObserver instance to handle component resizing.
     * @type {?ResizeObserver}
     * 
     * @memberof ContentPager
     * @private
     */
    #resizeObserver;

    // Exposed for subclasses.
    /**
     * The current page number being displayed.
     * @type {number}
     */
    currentPage = 1;

    /**
     * The main navigation container div.
     * @type {HTMLDivElement}
     */
    navigation;

    /**
     * Total number of pages.
     * @type {number}
     */
    totalPages = 0;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of ContentPager.
     * Initializes navigation elements and sets up event handling.
     */
    constructor() {
        super();

        // Register locale namespaces used by this component.
        this.#locales = this.registerLocaleNamespace();
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof ContentPager
     */
    static tagName = 'content-pager';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance
     * 
     * @memberof ContentPager
     */
    toString() {
        return '[ContentPager]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class
     * 
     * @static
     */
    static toString() {
        return '[ContentPager]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class
     * 
     * @static
     */
    static version() {
        return '1.0.0';
    }
    // #endregion ********************************************************************************/

    // #region: Static Properties ****************************************************************/
    /**
     * Defines the observed attributes for the custom element.
     * @returns {string[]} Array of attribute names to observe
     * 
     * @static
     */
    static get observedAttributes() {
        return [...(super.observedAttributes || []), 'current-page', 'max-visible', 'total-pages'];
    }
    // #endregion ********************************************************************************/

    // #region: Properties ***********************************************************************/
    /**
     * Gets the current page number.
     * @returns {number} The current page number
     * 
     * @memberof ContentPager
     */
    get currentPageNumber() {
        return parseInt(this.getAttribute('current-page'), 10) || 1;
    }

    /**
     * Sets the current page number.
     * @param {string|number} value The page number
     * 
     * @memberof ContentPager
     */
    set currentPageNumber(value) {
        const pageNum = Number(value);
        if (pageNum > 0 && pageNum <= this.totalPageCount) {
            this.setAttribute('current-page', pageNum.toString());
        }
    }

    /**
     * Gets the maximum visible page numbers in the page navigation.
     * @returns {number} The number of pages
     * 
     * @memberof ContentPager
     */
    get maxVisible() {
        return parseInt(this.getAttribute('max-visible'), 10) || ContentPager.DEFAULT_MAX_VISIBLE;
    }

    /**
     * Sets the maximum visible page numbers in the page navigation.
     * @param {string|number} value The number of pages
     * 
     * @memberof ContentPager
     */
    set maxVisible(value) {
        this.setAttribute('max-visible', Number(value) > 0 ? value.toString() : ContentPager.DEFAULT_MAX_VISIBLE.toString());
    }

    /**
     * Gets the total number of pages.
     * @returns {number} The total page count
     * 
     * @memberof ContentPager
     */
    get totalPageCount() {
        return parseInt(this.getAttribute('total-pages'), 10) || 0;
    }

    /**
     * Sets the total number of pages.
     * @param {string|number} value The total page count
     * 
     * @memberof ContentPager
     */
    set totalPageCount(value) {
        const pageCount = Number(value);
        this.setAttribute('total-pages', pageCount >= 0 ? pageCount.toString() : '0');
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Called when observed attributes change.
     * @param {string} name The name of the attribute that changed
     * @param {string|null} oldValue The previous value of the attribute
     * @param {string|null} newValue The new value of the attribute
     * 
     * @memberof ContentPager
     */
    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);

        if (oldValue === newValue) {
            return;
        }

        switch (name) {

            case 'current-page':
                this.currentPage = this.currentPageNumber;
                this.#updatePageNumbers();
                this.#updateNavigationState();

                break;

            case 'max-visible':
                this.#updatePageNumbers();

                break;

            case 'total-pages':
                this.totalPages = this.totalPageCount;
                this.#updatePageNumbers();
                this.#updateNavigationState();
                
                if (this.totalPages > 0 && !this.#navigationButtonWidth) {
                    this.#navigationButtonWidth = this.#calculateButtonWidth();
                }

                break;

            default:
                break;
        
        }
    }

    /**
     * Called when the custom element is attached to the DOM.
     * Sets up resize observer and initializes component.
     * 
     * @memberof ContentPager
     */
    connectedCallback() {
        super.connectedCallback();

        // Create navigation container.
        this.navigation = document.createElement('div');
        this.navigation.className = 'content-pager-navigation navigation-container';
        this.appendChild(this.navigation);

        // Setup navigation elements.
        this.#setupNavigation();

        // Sync properties with attributes.
        this.currentPage = this.currentPageNumber;
        this.totalPages = this.totalPageCount;

        // Calculate the navigation button size.
        if (this.totalPages > 0) {
            this.#navigationButtonWidth = this.#calculateButtonWidth();
        }

        // Add resize observer for the component.
        this.#resizeObserver = new ResizeObserver(this.#handleResize.bind(this));
        this.#resizeObserver.observe(this);

        // Initial update.
        this.#updatePageNumbers();
        this.#updateNavigationState();
    }

    /**
     * Cleanup. Called when the component is removed from the DOM.
     * Disconnects observers and removes event listeners.
     * 
     * @memberof ContentPager
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        // Disconnect observers.
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
        }
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Navigates to a specific page number and dispatches page-changed event.
     * @param {number} pageNum The page number to navigate to
     * 
     * @memberof ContentPager
     */
    handleGoToPage = (pageNum) => {
        if (pageNum >= 1 && pageNum <= this.totalPages && pageNum !== this.currentPage) {
            this.currentPageNumber = pageNum;

            // Dispatch page-changed event.
            this.dispatchEvent(new CustomEvent('page-changed', {
                bubbles: true,
                detail: {
                    currentPage: pageNum,
                    previousPage: this.currentPage
                }
            }));
        }
    };

    /**
     * Handles page navigation to the next page.
     * @param {Event} event The event data
     * 
     * @memberof ContentPager
     */
    handleNextPage = (event) => {
        if (this.currentPage >= this.totalPages) {
            return;
        }

        event.stopPropagation();
        this.handleGoToPage(this.currentPage + 1);
    };

    /**
     * Handles page navigation to the previous page.
     * @param {Event} event The event data
     * 
     * @memberof ContentPager
     */
    handlePrevPage = (event) => {
        if (this.currentPage <= 1) {
            return;
        }

        event.stopPropagation();
        this.handleGoToPage(this.currentPage - 1);
    };

    /**
     * Handle resize events.
     * Updates page numbers display based on available space.
     * 
     * @memberof ContentPager
     * @private
     */
    #handleResize = () => {
        this.#updatePageNumbers();
    };
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Calculates the width needed for a page number button.
     * This is used to determine if a dropdown is needed for page navigation.
     * @returns {number} The width in pixels needed for a single button
     * 
     * @memberof ContentPager
     * @private
     */
    #calculateButtonWidth() {
        const tempButton = document.createElement('button');

        tempButton.className = 'page-number';
        tempButton.style.visibility = 'hidden';
        tempButton.style.position = 'absolute';

        // Use the largest page number for calculation.
        tempButton.textContent = this.totalPages.toString();

        // Fallback to `this` if #pageNumbers not yet in DOM.
        const parent = this.#pageNumbers || this;

        parent.appendChild(tempButton);

        const buttonWidth = tempButton.offsetWidth;
        parent.removeChild(tempButton);

        return buttonWidth;
    }

    /**
     * Creates a navigation button with the specified properties.
     * @param {string} className Class name suffix for the button (e.g., 'first', 'prev')
     * @param {string} title Title and aria-label for the button
     * @param {Function} clickHandler Function to handle clicks on the button
     * @returns {HTMLButtonElement} The created button
     * 
     * @memberof ContentPager
     * @private
     */
    #createNavigationButton(className, title, clickHandler) {
        const button = document.createElement('button');

        button.className = `nav-button ${className}`;
        button.setAttribute('aria-label', title);
        button.setAttribute('title', title);
        button.onclick = clickHandler;

        return button;
    }

    /**
     * Sets up the navigation controls (buttons, page numbers) for the content pager.
     * Uses the exact same structure as the original pdf-viewer navigation.
     * 
     * @memberof ContentPager
     * @private
     */
    #setupNavigation() {
        const navigationGroup = document.createElement('div');

        // Use the shared class name for styling compatibility.
        navigationGroup.className = 'navigation-group';

        this.#firstButton = this.#createNavigationButton('first', this.#locales?.firstPage || 'Eerste pagina', () => {
            return this.handleGoToPage(1);
        });
        this.#prevButton = this.#createNavigationButton('prev', this.#locales?.previousPage || 'Vorige pagina', this.handlePrevPage);
        this.#nextButton = this.#createNavigationButton('next', this.#locales?.nextPage || 'Volgende pagina', this.handleNextPage);
        this.#lastButton = this.#createNavigationButton('last', this.#locales?.lastPage || 'Laatste pagina', () => {
            return this.handleGoToPage(this.totalPages);
        });

        this.#pageNumbers = document.createElement('div');
        this.#pageNumbers.className = 'page-numbers';

        this.#pageCount = document.createElement('div');
        this.#pageCount.className = 'page-count';

        navigationGroup.append(this.#firstButton, this.#prevButton, this.#pageNumbers, this.#nextButton, this.#lastButton);
        this.navigation.appendChild(navigationGroup);
        this.navigation.appendChild(this.#pageCount);

        this.navigation.style.containerType = 'inline-size';
        this.navigation.style.containerName = 'navigation-container';
    }

    /**
     * Updates the navigation state (enabled/disabled states of buttons) based on current page and total pages.
     * 
     * @memberof ContentPager
     * @private
     */
    #updateNavigationState() {
        if (!this.#firstButton) {
            return;
        }

        const isFirstPage = this.currentPage === 1;
        const isLastPage = this.currentPage === this.totalPages;
        const hasSinglePageOrNoPages = this.totalPages <= 1;

        this.#firstButton.disabled = isFirstPage || hasSinglePageOrNoPages;
        this.#prevButton.disabled = isFirstPage || hasSinglePageOrNoPages;
        this.#nextButton.disabled = isLastPage || hasSinglePageOrNoPages;
        this.#lastButton.disabled = isLastPage || hasSinglePageOrNoPages;

        this.#updatePageCount();
    }

    /**
     * Updates the page count display with current total pages.
     * 
     * @memberof ContentPager
     * @private
     */
    #updatePageCount() {
        if (!this.#pageCount) {
            return;
        }

        const pageLabelMultiple = this.#locales?.pages || 'paginas';
        const pageLabelSingle = this.#locales?.page || 'pagina';

        if (this.totalPages === 1) {
            this.#pageCount.textContent = `${this.totalPages} ${pageLabelSingle}`;
        }
        else if (this.totalPages > 1) {
            this.#pageCount.textContent = `${this.totalPages} ${pageLabelMultiple}`;
        }
        else {
            this.#pageCount.textContent = '';
        }
    }

    /**
     * Updates the page number display based on available space.
     * Dynamically switches between page numbers and dropdown based on available width.
     * 
     * @memberof ContentPager
     * @private
     */
    #updatePageNumbers() {
        if (!this.#pageNumbers || this.totalPages <= 0) {
            return;
        }

        // If we only have one page, don't show any page numbers.
        if (this.totalPages === 1) {
            this.#pageNumbers.innerHTML = '';
            this.navigation.classList.remove('use-dropdown');
            return;
        }

        const availableWidth = this.navigation.clientWidth;
        const navButtonWidth = this.#firstButton?.offsetWidth || ContentPager.BUTTON_SIZE;
        const pageCountWidth = this.#pageCount?.offsetWidth || 0;
        const { maxVisible } = this;

        // Calculate navigation button width if not set
        if (!this.#navigationButtonWidth && this.totalPages > 0) {
            this.#navigationButtonWidth = this.#calculateButtonWidth();
        }

        const buttonWidth = this.#navigationButtonWidth || 50;
        
        /* eslint-disable no-magic-numbers -- These are values that are known, no need to create variables for it. */
        // Calculate the minimum space needed for page number buttons.
        const pagesDisplayed = Math.min(maxVisible, this.totalPages);
        const ellipsisCount = this.totalPages > maxVisible ? 2 : 0;

        // Navigation group gap + page numbers gap.
        const gapsBetweenElements = 8;

        const minSpaceNeeded =
            // Navigation buttons (first, prev, next, last).
            (4 * navButtonWidth) +
            // Page number buttons or ellipsis.
            ((pagesDisplayed + ellipsisCount) * buttonWidth) +
            // Page count display.
            pageCountWidth +
            // Gaps and margins.
            gapsBetweenElements * 8;

        // Minimum width for navigation buttons to prevent wrapping (4 nav buttons + gaps).
        // 4 buttons + 3 gaps of 8px.
        const minNavigationWidth = (4 * navButtonWidth) + (3 * 8);

        /* eslint-enable no-magic-numbers */

        // Use dropdown if space is insufficient OR if width is too small for navigation buttons.
        const useDropdown = minSpaceNeeded > availableWidth || availableWidth < minNavigationWidth;

        this.navigation.classList.toggle('use-dropdown', useDropdown);
        this.#pageNumbers.innerHTML = '';

        if (useDropdown) {
            this.#createPageSelectDropdown();
        } else {
            this.#createPageNumberButtons();
        }
    }

    /**
     * Creates a dropdown select for page navigation when space is limited.
     * 
     * @memberof ContentPager
     * @private
     */
    #createPageSelectDropdown() {
        const select = document.createElement('select');
        const selectPageLabel = this.#locales?.selectPage || 'Selecteer pagina';

        select.className = 'page-select form-control';
        select.setAttribute('aria-label', selectPageLabel);
        select.setAttribute('title', selectPageLabel);

        for (let i = 1; i <= this.totalPages; i++) {
            const option = document.createElement('option');

            option.value = i;
            option.textContent = i;
            option.selected = i === this.currentPage;
            select.appendChild(option);
        }

        select.addEventListener('change', (event) => {
            return this.handleGoToPage(parseInt(event.target.value, 10));
        }, { capture: false });

        this.#pageNumbers.appendChild(select);
    }

    /**
     * Creates individual page number buttons when space is available.
     * 
     * @memberof ContentPager
     * @private
     */
    #createPageNumberButtons() {
        const { maxVisible } = this;
        const pageNumberArray = [];

        let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
        const end = Math.min(this.totalPages, start + maxVisible - 1);

        // Adjust start if we don't have enough pages to fill maxVisible.
        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        // Add ellipsis for pages before the visible range.
        if (start > 1) {
            const jumpTo = Math.max(1, start - maxVisible);
            pageNumberArray.push({ type: 'ellipsis', value: jumpTo });
        }

        // Add visible page numbers.
        for (let i = start; i <= end; i++) {
            pageNumberArray.push({ type: 'number', value: i });
        }

        // Add ellipsis for pages after the visible range.
        if (end < this.totalPages) {
            const jumpTo = Math.min(this.totalPages, end + maxVisible);
            pageNumberArray.push({ type: 'ellipsis', value: jumpTo });
        }

        // Create and append buttons.
        pageNumberArray.forEach((item) => {
            const button = document.createElement('button');

            if (item.type === 'ellipsis') {
                button.textContent = '...';
                button.className = 'ellipsis';
                button.setAttribute('aria-label', `Go to page ${item.value}`);
            }
            else {
                button.textContent = item.value;
                button.className = `page-number ${item.value === this.currentPage ? 'selected' : ''}`;
                button.setAttribute('aria-label', `Go to page ${item.value}`);
                
                if (item.value === this.currentPage) {
                    button.setAttribute('aria-current', 'page');
                }
            }

            button.onclick = () => {
                return this.handleGoToPage(item.value);
            };

            this.#pageNumbers.appendChild(button);
        });
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register web component.
 * Defines the custom element 'content-pager' and maps it to the ContentPager class.
 */
customElements.define(ContentPager.tagName, ContentPager);
